import { memo, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Grid, Group, Skeleton, Stack, Table, Text, Title } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';

import type {
  EnvironmentalCalculations,
  EnvironmentalDataFormData,
  PrimaryFuelDerivedEntry,
} from '../../../types/reports.js';
import { fuelAshCategoriesApi, type FuelAshCategory } from '../../../services/fuelAshCategoriesApi.js';
import { getApiErrorMessage } from '../../../utils/errorHandling.js';
import {
  MATRIX_LABEL_TH,
  MATRIX_TABLE_STYLES,
  MATRIX_VALUE_TH,
  MATRIX_VALUE_TH_NARROW,
  MATRIX_WASTE_CODE_TH,
} from '../../form/matrixPresets.js';
import { FUEL_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import { ValidatedNumberInput } from '../../form/validatedFields.js';
import { RegisterFieldTab } from '../../form/RegisterFieldTab.js';
import { FormSubsection } from '../../ui/FormSubsection.js';
import { AlternativeFuelRow } from './AlternativeFuelRow.js';
import {
  applyCategoryChange,
  createAlternativeFuelRow,
  entryCountLabel,
  groupRowsByCategory,
} from './alternativeFuelRows.js';
import { findAlternativeFuelDerived, findPrimaryFuelDerived, formatDerivedCell, fuelRowCalcSig } from './fuelDerived.js';
import { collectUsedWasteCodes, usedWasteCodesSignature } from './usedWasteCodes.js';

type FuelForm = UseFormReturnType<EnvironmentalDataFormData>;

interface BrennstoffeSectionProps {
  form: FuelForm;
  // Serverseitig berechnete Ableitungen der drei Brennstofftabellen (thermischer
  // Energieeinsatz, Brennstoffasche, biogene CO2). Werden NIE in form geschrieben --
  // reine Anzeige, siehe WasserSection.tsx als Vorbild fuer dasselbe Muster.
  fuelCalc: EnvironmentalCalculations | null;
  disabled?: boolean;
  loading?: boolean;
}

// Feld-Overrides zu FUEL_INPUT_PROPS als Modul-Konstanten (stabile Referenz + D-05):
// emissionFactor braucht 6 Nachkommastellen, biogener Anteil ist auf 100 % gedeckelt.
const EMISSION_FACTOR_INPUT_PROPS = { ...FUEL_INPUT_PROPS, decimalScale: 6 } as const;
const ASH_CONTENT_INPUT_PROPS = { ...FUEL_INPUT_PROPS, decimalScale: 2 } as const;

// Primary fuel types with German labels. Katalog-Reihenfolge = feste Array-Reihenfolge
// fuer das dichte primaryFuels-Binding (siehe PRIMARY_FUEL_INDEX unten und
// hydratePrimaryFuels in ReportEditPage.tsx, dort als Single-Source-Katalog importiert).
export const PRIMARY_FUEL_TYPES = [
  { key: 'hardCoal', label: 'Steinkohle' },
  { key: 'brownCoal', label: 'Braunkohle' },
  { key: 'petCoke', label: 'Petrolkoks' },
  { key: 'heavyOil', label: 'Schweröl' },
  { key: 'lightOil', label: 'Leichtöl' },
  { key: 'naturalGas', label: 'Erdgas' },
  { key: 'otherConventional', label: 'Sonstige konventionelle' },
];

// Primary fuel categories (aeussere Schleife der Kategorie x Typ Indexberechnung).
export const PRIMARY_FUEL_CATEGORIES = [
  { key: 'clinker', label: 'Klinkerproduktion' },
  { key: 'rawMaterialDrying', label: 'Rohstofftrocknung' },
  { key: 'cementComponentDrying', label: 'Zementbestandteiltrocknung' },
  { key: 'spaceHeating', label: 'Raumheizung und Werksfahrzeuge' },
];

// PRIMARY_FUEL_INDEX: Kategorie aussen, Typ innen -> laufender Index 0..27. IDENTISCHE
// Reihenfolge wie hydratePrimaryFuels in ReportEditPage.tsx (dort als Quelle importiert).
const PRIMARY_FUEL_INDEX = new Map<string, number>(
  PRIMARY_FUEL_CATEGORIES.flatMap((category, catIdx) =>
    PRIMARY_FUEL_TYPES.map(
      (fuel, fuelIdx) =>
        [`${category.key}|${fuel.key}`, catIdx * PRIMARY_FUEL_TYPES.length + fuelIdx] as [
          string,
          number,
        ],
    ),
  ),
);

// D-19/Sketch 017 (index.html:688/690): die beiden Zeitkonstanten der Hervorhebung. Kein
// frei gewaehlter Wert -- beide stammen aus dem massgeblichen Entwurf. Die Hervorhebung
// beantwortet "wohin ist meine Zeile gesprungen?", wenn ein Kategoriewechsel sie in einen
// anderen Tabellenabschnitt verschiebt (oder eine neu angelegte Zeile erscheint) -- kein
// Zierrat, deshalb bewusst nicht in einer spaeteren Aufraeumrunde als Mikrobewegung ohne
// Nutzen zu streichen.
const ROW_HIGHLIGHT_DURATION_MS = 1300;
const ROW_FOCUS_DELAY_MS = 30;

// D-23 (Checkpoint Plan 34-14): ob die vier in der Gruppen-/Gesamtzeile nicht belegten
// Zellen (Heizwert, Em.-Faktor, Biogen, Aschegehalt -- die vier gewichteten Mittel aus dem
// Sketch entfallen nach D-20) leer bleiben oder ein gedaempftes "—" tragen, entscheidet der
// Nutzer am Bildschirm. Hinter dieser einen Konstante umschaltbar, ohne Codeumbau, damit der
// Checkpoint beide Varianten zeigen kann. Ausgangswert nach Vorschlag CONTEXT.md: "—".
const GROUP_ROW_EMPTY_CELL_TEXT: string = '—';

// Fehler-Signatur einer Zeile: verkettet die Backend-Fehlermeldungen der Feldpfade der Zeile
// zu einem String. Dient AUSSCHLIESSLICH als Re-Render-Ausloeser fuer die memoisierten Rows:
// beim Tippen aendert sich form.errors nicht (runValidation laeuft nur onBlur) -> Signatur
// stabil -> memo ueberspringt das Re-Render der ~159 NumberInputs. Beim Blur setzt
// runValidation die Fehler -> Signatur der betroffenen Zeile aendert sich -> nur diese Zeile
// re-rendert und wird rot. Genau dieser Prop verhindert den Memoization-Fallstrick
// (verlorene Fehleranzeige), weil form selbst pro Render eine neue Referenz ist und daher
// im Comparator bewusst ignoriert wird.
function rowErrorSig(errors: Record<string, unknown>, paths: string[]): string {
  return paths.map((p) => String(errors[p] ?? '')).join('|');
}

interface PrimaryFuelRowProps {
  form: FuelForm;
  label: string;
  idx: number;
  disabled?: boolean;
  errorSig: string;
  derived: PrimaryFuelDerivedEntry | undefined;
  calcSig: string;
}

const PrimaryFuelRow = memo(
  function PrimaryFuelRow({ form, label, idx, disabled, derived }: PrimaryFuelRowProps) {
    return (
      <Table.Tr>
        <Table.Td>
            <Text size="sm">{label}</Text>
        </Table.Td>
        <Table.Td>
          <ValidatedNumberInput
            form={form}
            field={`primaryFuels.${idx}.amount`}
            kind="decimal"
            unit="t"
            align="right"
            disabled={disabled}
            {...FUEL_INPUT_PROPS}
          />
        </Table.Td>
        <Table.Td>
          <ValidatedNumberInput
            form={form}
            field={`primaryFuels.${idx}.heatingValue`}
            kind="decimal"
            unit="MJ/kg"
            align="right"
            disabled={disabled}
            {...FUEL_INPUT_PROPS}
          />
        </Table.Td>
        <Table.Td>
          <ValidatedNumberInput
            form={form}
            field={`primaryFuels.${idx}.emissionFactor`}
            kind="decimal"
            unit="t/TJ"
            align="right"
            disabled={disabled}
            {...EMISSION_FACTOR_INPUT_PROPS}
          />
        </Table.Td>
        <Table.Td>
          <ValidatedNumberInput
            form={form}
            field={`primaryFuels.${idx}.ashContent`}
            kind="decimal"
            unit="%"
            align="right"
            disabled={disabled}
            {...ASH_CONTENT_INPUT_PROPS}
          />
        </Table.Td>
        <Table.Td>
          {/* Anzeigezelle statt schreibgeschuetztem NumberInput: die Primaermatrix zaehlt 28
              Zeilen, zwei zusaetzliche Eingabefelder je Zeile waeren 56 weitere Mantine-Inputs
              in einem Konstrukt, das laut Kommentar oben bereits ~144 traegt (Phase 27, D-12).
              Ein Text-Knoten ist zudem das im Projekt uebliche Muster fuer abgeleitete Werte in
              Tabellen (FormularDTotal). Serverseitig berechnet, nie aus form gelesen oder
              dorthin zurueckgeschrieben. */}
          <Text size="sm" ta="right">{formatDerivedCell(derived?.thermalEnergy)}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" ta="right">{formatDerivedCell(derived?.fuelAsh)}</Text>
        </Table.Td>
      </Table.Tr>
    );
  },
  // calcSig ergaenzt (T-30-13): ohne diesen Vergleich aendert sich fuer eine memoisierte Zeile
  // nach der Server-Antwort keiner der uebrigen Props (form ist stabil referenziert, errorSig
  // und idx bleiben gleich) -- die Zeile zeigte dann weiterhin den alten oder leeren Wert, weil
  // der Comparator die neuen derived-Werte gar nicht kennt. Derselbe Fallstrick wie beim
  // bestehenden usedCodesSig-Kommentar bei AlternativeFuelRow.
  (prev, next) =>
    prev.errorSig === next.errorSig &&
    prev.disabled === next.disabled &&
    prev.idx === next.idx &&
    prev.calcSig === next.calcSig,
);

export function BrennstoffeSection({ form, fuelCalc, disabled, loading }: BrennstoffeSectionProps) {
  const rows = form.getValues().alternativeFuels ?? [];
  // form.errors treibt die per-Zeile-Fehlersignatur; beim Tippen unveraendert (setErrors nur
  // via onBlur-runValidation) -> memoisierte Zeilen ueberspringen das Re-Render.
  const errors = form.errors as Record<string, unknown>;

  // Belegungszustand fuer D-19 (Abfallschluessel): welcher AVV-Schluessel ist bereits in
  // welcher Zeile vergeben. Bumpt bei einer Codeauswahl/-loeschung und bei Add/Remove, NICHT
  // beim Tippen in einem Zahlenfeld (das uncontrolled-Muster aus Phase 18 bleibt unberuehrt).
  const [usedCodes, setUsedCodes] = useState(() => collectUsedWasteCodes(form.getValues()));
  const usedCodesSig = usedWasteCodesSignature(usedCodes);

  // Fernabruf der zwoelf Sekundaer-Kategorien (Phase 34, D-01/D-02): eigener Lade-/Fehler-
  // zustand mit `useState`, angelehnt an FuelAshCategoriesPanel.tsx. Das ist bewusst KEIN
  // Verstoss gegen die Formularregel (kein handverwaltetes useState fuer Feldwerte) -- die
  // Kategorienliste ist kein Formularfeld, sondern Stammdaten aus einem GET-Request, genau
  // wie der bestehende `loading`-Prop fuer die Meldung selbst.
  const [categories, setCategories] = useState<FuelAshCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  const [justHighlightedRowId, setJustHighlightedRowId] = useState<string | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setCategoriesLoading(true);
        setCategoriesError(null);
        const result = await fuelAshCategoriesApi.getSecondaryForForm();
        if (cancelled) return;
        setCategories(result);
      } catch (err) {
        if (!cancelled) {
          setCategoriesError(getApiErrorMessage(err, 'Fehler beim Laden der Brennstoffkategorien'));
        }
      } finally {
        if (!cancelled) {
          setCategoriesLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
    // Laedt genau einmal beim Mounten -- dieselbe Begruendung wie FuelAshCategoriesPanel.tsx.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Zeitgeber aufraeumen: ohne diesen Effekt liefe ein Hervorhebungs-/Fokus-Timer nach dem
  // Aushaengen der Komponente weiter und setzte Zustand auf einer entsorgten Instanz.
  useEffect(
    () => () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    },
    [],
  );

  function refreshUsedCodes(): void {
    setUsedCodes(collectUsedWasteCodes(form.getValues()));
  }

  // D-19: Hervorhebung (1300 ms) plus Fokus auf das Bezeichnungsfeld nach 30 ms (Sketch 017,
  // index.html:690) -- fuer eine neu angelegte Zeile UND nach einem Kategoriewechsel. Der
  // Fokus muss NACH dem Neuaufbau der Tabelle gesetzt werden, sonst greift er ins Leere;
  // React committet den State-Update des Klick-Handlers synchron beim Verlassen des
  // Handlers, ein `setTimeout` danach findet die Zeile deshalb bereits an ihrer neuen
  // Position. Das Scrollen laeuft ueber denselben CSS-Hook (`.alternative-fuel-row--just-
  // added`, gesetzt von AlternativeFuelRow.tsx ueber die `justAdded`-Prop) statt einer
  // zweiten, an dieser Datei vorbeigefuehrten Positionsermittlung.
  function highlightRow(rowId: string): void {
    setJustHighlightedRowId(rowId);

    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => {
      setJustHighlightedRowId((current) => (current === rowId ? null : current));
    }, ROW_HIGHLIGHT_DURATION_MS);

    // Scrollen so bald wie moeglich (naechster Microtask): die Zeile wechselt zwangslaeufig
    // den Tabellenabschnitt (Kategoriewechsel) oder erscheint neu -- ohne diesen Hinweis
    // verschwindet sie aus dem Blickfeld des Melders, "wohin ist meine Zeile gesprungen?".
    queueMicrotask(() => {
      document
        .querySelector<HTMLElement>('.alternative-fuel-row--just-added')
        ?.scrollIntoView({ block: 'nearest' });
    });

    if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    focusTimerRef.current = setTimeout(() => {
      const row = document.querySelector<HTMLElement>('.alternative-fuel-row--just-added');
      row?.querySelector<HTMLInputElement>('input')?.focus();
    }, ROW_FOCUS_DELAY_MS);
  }

  function handleAddRow(category: FuelAshCategory): void {
    const row = createAlternativeFuelRow(category.id, category.startValue ?? null, () => crypto.randomUUID());
    form.insertListItem('alternativeFuels', row);
    refreshUsedCodes();
    highlightRow(row.rowId);
  }

  function handleRemoveRow(flatIndex: number): void {
    form.removeListItem('alternativeFuels', flatIndex);
    // Eine geloeschte Zeile gibt ihren Schluessel frei, und der Index aller nachfolgenden
    // Zeilen (und damit ihr Fundstellen-Label) verschiebt sich -- die Map muss neu gebaut
    // werden.
    refreshUsedCodes();
  }

  function handleChangeCategory(flatIndex: number, newCategoryId: number): void {
    const currentRows = form.getValues().alternativeFuels ?? [];
    const row = currentRows[flatIndex];
    if (!row) return;

    const oldCategory = categories.find((c) => c.id === row.categoryId);
    const newCategory = categories.find((c) => c.id === newCategoryId);
    if (!oldCategory || !newCategory) return;

    // D-17/D-18: die Regel selbst liegt in alternativeFuelRows.ts, hier wird sie nur
    // angewandt. `null` heisst "dieselbe Kategorie", nichts zu tun.
    const change = applyCategoryChange(row, oldCategory, newCategory);
    if (!change) return;

    form.setFieldValue(`alternativeFuels.${flatIndex}.categoryId`, change.categoryId);
    form.setFieldValue(`alternativeFuels.${flatIndex}.ashContent`, change.ashContent);
    highlightRow(row.rowId);
  }

  if (loading) {
    return (
      <Grid>
        {[0, 1, 2, 3].map((i) => (
          <Grid.Col span={12} key={i}>
            <Skeleton height={100} />
          </Grid.Col>
        ))}
      </Grid>
    );
  }

  const groups = groupRowsByCategory(rows, categories);
  const totalSums = fuelCalc?.alternativeFuelTotalSums;

  return (
    <Stack gap="md">
      <FormSubsection title="Primärbrennstoffe">
        <RegisterFieldTab field="primaryFuels" label="Primärbrennstoffe" />
        {/* Die Kategorien stehen so weit auseinander wie zwei FormSubsections —
            gleiche Quelle (--form-subsection-spacing in index.css), kein zweiter Wert. */}
        <Stack gap="var(--form-subsection-spacing)">
          {PRIMARY_FUEL_CATEGORIES.map((category) => (
            <Stack gap="xs" key={category.key}>
              <Title order={5}>{category.label}</Title>
                <Box style={{ overflowX: 'auto' }}>
                    <Table striped="odd"  styles={MATRIX_TABLE_STYLES}>
                    <Table.Thead>
                      <Table.Tr>
                          <Table.Th style={MATRIX_LABEL_TH}>Brennstoff</Table.Th>
                          <Table.Th style={MATRIX_VALUE_TH}>Menge (t)</Table.Th>
                          <Table.Th style={MATRIX_VALUE_TH}>Heizwert (MJ/kg)</Table.Th>
                          <Table.Th style={MATRIX_VALUE_TH}>CO2-Emissionsfaktor (t CO2 / TJ)</Table.Th>
                          <Table.Th style={MATRIX_VALUE_TH}>Durchschnittlicher Aschegehalt (%)</Table.Th>
                          <Table.Th style={MATRIX_VALUE_TH}>Thermischer Energieeinsatz (1.000 GJ)</Table.Th>
                          <Table.Th style={MATRIX_VALUE_TH}>Brennstoffasche (t)</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {PRIMARY_FUEL_TYPES.map((fuel) => {
                        const idx = PRIMARY_FUEL_INDEX.get(`${category.key}|${fuel.key}`) ?? 0;
                        const derived = findPrimaryFuelDerived(
                          fuelCalc?.primaryFuelsDerived,
                          category.key,
                          fuel.key,
                        );
                        return (
                          <PrimaryFuelRow
                            key={fuel.key}
                            form={form}
                            label={fuel.label}
                            idx={idx}
                            disabled={disabled}
                            derived={derived}
                            calcSig={fuelRowCalcSig(derived)}
                            errorSig={rowErrorSig(errors, [
                              `primaryFuels.${idx}.amount`,
                              `primaryFuels.${idx}.heatingValue`,
                              `primaryFuels.${idx}.emissionFactor`,
                              `primaryFuels.${idx}.ashContent`,
                            ])}
                          />
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Box>
            </Stack>
          ))}
        </Stack>
      </FormSubsection>

      <FormSubsection title="Sekundär-/Ersatzbrennstoffe">
        <RegisterFieldTab field="alternativeFuels" label="Sekundär-/Ersatzbrennstoffe" />
        {categoriesError ? (
          // Eine Tabelle ohne Gruppen waere nicht von "keine Kategorien gepflegt" zu
          // unterscheiden -- der Fehlerfall bekommt deshalb eine eigene, sichtbare Meldung
          // statt einer leeren Tabelle.
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Kategorien konnten nicht geladen werden">
            {categoriesError}
          </Alert>
        ) : categoriesLoading ? (
          // Solange die Kategorienliste laedt, gaebe es weder Gruppennamen noch einen
          // Aschegehalt-Startwert zum Vorbelegen -- dieselbe Skelettdarstellung wie beim
          // `loading`-Prop, statt einer Tabelle ohne jede Gruppe.
          <Grid>
            {[0, 1, 2].map((i) => (
              <Grid.Col span={12} key={i}>
                <Skeleton height={60} />
              </Grid.Col>
            ))}
          </Grid>
        ) : (
          <Box style={{ overflowX: 'auto' }}>
            <Table striped="odd" styles={MATRIX_TABLE_STYLES}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={MATRIX_LABEL_TH}>Kategorie / Bezeichnung</Table.Th>
                  <Table.Th style={MATRIX_WASTE_CODE_TH}>Abfallschlüssel</Table.Th>
                  <Table.Th style={MATRIX_VALUE_TH_NARROW}>Menge (t)</Table.Th>
                  <Table.Th style={MATRIX_VALUE_TH_NARROW}>Heizwert (MJ/kg)</Table.Th>
                  <Table.Th style={MATRIX_VALUE_TH_NARROW}>Em.-Faktor (t/TJ)</Table.Th>
                  <Table.Th style={MATRIX_VALUE_TH_NARROW}>Biogen (%)</Table.Th>
                  <Table.Th style={MATRIX_VALUE_TH_NARROW}>Aschegehalt (%)</Table.Th>
                  <Table.Th style={MATRIX_VALUE_TH_NARROW}>Therm. Energie (1.000 GJ)</Table.Th>
                  <Table.Th style={MATRIX_VALUE_TH_NARROW}>Asche (t)</Table.Th>
                  <Table.Th style={MATRIX_VALUE_TH_NARROW}>Biogene CO₂ (t)</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {groups.flatMap((group) => {
                  const categorySum = group.category
                    ? fuelCalc?.alternativeFuelCategorySums?.find((sum) => sum.categoryId === group.category!.id)
                    : undefined;

                  // D-02: jede uebergebene Kategorie steht als eigene Gruppenzeile da, auch
                  // ohne eine einzige Zeile. T-34-11: eine Zeile mit geloeschter Kategorie
                  // landet in der synthetischen orphan-Gruppe (`group.category === undefined`)
                  // -- ohne eigene Gruppenzeile mit Zaehler/Summen/Anlegen-Knopf, sie zeigt
                  // nur ihre vorhandenen Zeilen, damit sie nicht aus der Anzeige verschwindet.
                  const groupRowElement = group.category ? (
                    <Table.Tr key={`group-${group.category.id}`} className="alternative-fuel-group-row">
                      <Table.Td colSpan={2}>
                        <Group gap="xs" wrap="nowrap">
                          <Text fw={600}>{group.category.name}</Text>
                          {group.rows.length > 0 && (
                            <Text size="sm" c="dimmed">
                              {entryCountLabel(group.rows.length)}
                            </Text>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ta="right" fw={600}>{formatDerivedCell(categorySum?.amount)}</Text>
                      </Table.Td>
                      {/* D-20: die vier gewichteten Mittel aus dem Sketch (Heizwert, Em.-Faktor,
                          Biogen, Aschegehalt) entfallen -- niemand hat sie bestellt, sie sehen
                          aus wie ein Messwert, sind aber ein Konstrukt. D-23: Platzhaltertext
                          hinter GROUP_ROW_EMPTY_CELL_TEXT. */}
                      <Table.Td><Text size="sm" ta="right" c="dimmed">{GROUP_ROW_EMPTY_CELL_TEXT}</Text></Table.Td>
                      <Table.Td><Text size="sm" ta="right" c="dimmed">{GROUP_ROW_EMPTY_CELL_TEXT}</Text></Table.Td>
                      <Table.Td><Text size="sm" ta="right" c="dimmed">{GROUP_ROW_EMPTY_CELL_TEXT}</Text></Table.Td>
                      <Table.Td><Text size="sm" ta="right" c="dimmed">{GROUP_ROW_EMPTY_CELL_TEXT}</Text></Table.Td>
                      <Table.Td>
                        <Text size="sm" ta="right" fw={600}>{formatDerivedCell(categorySum?.thermalEnergy)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ta="right" fw={600}>{formatDerivedCell(categorySum?.fuelAsh)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ta="right" fw={600}>{formatDerivedCell(categorySum?.biogenicCo2)}</Text>
                      </Table.Td>
                      <Table.Td>
                        {/* D-16/D-02: der Knopf sitzt IN der Gruppenzeile -- so bleibt eine
                            leere Kategorie genau eine Zeile hoch, statt eine eigene Zeile
                            darunter zu brauchen. T-34-16: deaktiviert, solange die
                            Kategorienliste laedt oder das Formular disabled ist -- ohne
                            geladene Kategorie gaebe es keinen Aschegehalt-Startwert. */}
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          onClick={() => handleAddRow(group.category!)}
                          disabled={disabled || categoriesLoading}
                        >
                          + Brennstoff
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ) : null;

                  const dataRowElements = group.rows.map(({ row, flatIndex }) => {
                    const derived = findAlternativeFuelDerived(fuelCalc?.alternativeFuelsDerived, row.rowId);
                    return (
                      <AlternativeFuelRow
                        key={row.rowId}
                        form={form}
                        flatIndex={flatIndex}
                        disabled={disabled}
                        usedCodes={usedCodes}
                        usedCodesSig={usedCodesSig}
                        onCodeChange={refreshUsedCodes}
                        onRemove={handleRemoveRow}
                        onChangeCategory={handleChangeCategory}
                        categories={categories}
                        currentCategoryId={row.categoryId}
                        derived={derived}
                        calcSig={fuelRowCalcSig(derived)}
                        justAdded={justHighlightedRowId === row.rowId}
                        errorSig={rowErrorSig(errors, [
                          `alternativeFuels.${flatIndex}.name`,
                          `alternativeFuels.${flatIndex}.wasteCode`,
                          `alternativeFuels.${flatIndex}.amount`,
                          `alternativeFuels.${flatIndex}.heatingValue`,
                          `alternativeFuels.${flatIndex}.emissionFactor`,
                          `alternativeFuels.${flatIndex}.biogenicContent`,
                          `alternativeFuels.${flatIndex}.ashContent`,
                        ])}
                      />
                    );
                  });

                  return groupRowElement ? [groupRowElement, ...dataRowElements] : dataRowElements;
                })}

                {/* D-21: Gesamtzeile am Tabellenende -- dieselben vier Summen ueber alle
                    Kategorien, ebenfalls in Graustufen, aber deutlich staerker abgesetzt
                    (fw={700} statt 600) als die Gruppenzeilen darueber. */}
                <Table.Tr className="alternative-fuel-total-row">
                  <Table.Td colSpan={2}>
                    <Text fw={700}>Gesamt</Text>
                  </Table.Td>
                  <Table.Td><Text size="sm" ta="right" fw={700}>{formatDerivedCell(totalSums?.amount)}</Text></Table.Td>
                  <Table.Td><Text size="sm" ta="right" c="dimmed">{GROUP_ROW_EMPTY_CELL_TEXT}</Text></Table.Td>
                  <Table.Td><Text size="sm" ta="right" c="dimmed">{GROUP_ROW_EMPTY_CELL_TEXT}</Text></Table.Td>
                  <Table.Td><Text size="sm" ta="right" c="dimmed">{GROUP_ROW_EMPTY_CELL_TEXT}</Text></Table.Td>
                  <Table.Td><Text size="sm" ta="right" c="dimmed">{GROUP_ROW_EMPTY_CELL_TEXT}</Text></Table.Td>
                  <Table.Td><Text size="sm" ta="right" fw={700}>{formatDerivedCell(totalSums?.thermalEnergy)}</Text></Table.Td>
                  <Table.Td><Text size="sm" ta="right" fw={700}>{formatDerivedCell(totalSums?.fuelAsh)}</Text></Table.Td>
                  <Table.Td><Text size="sm" ta="right" fw={700}>{formatDerivedCell(totalSums?.biogenicCo2)}</Text></Table.Td>
                  <Table.Td />
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Box>
        )}
      </FormSubsection>
    </Stack>
  );
}
